<script lang="ts" module>
  import { slide } from 'svelte/transition';
  import { Accordion, type AccordionProps } from 'melt/builders';

  import { type ExpandableCardItem } from '../card/ExpandableCard.svelte';
  import DimensionCard, { type DimensionCardProps } from './dimensionCard/DimensionCard.svelte';
  import FooterMoreActions from './footerMoreActions/FooterMoreActions.svelte';

  export type PillGroup = {
    used: boolean;
    pills: DimensionCardProps['pills'];
    label: DimensionCardProps['label'];
    score?: DimensionCardProps['score'];
  } & ExpandableCardItem;

  export interface AdditionalProps {
    open?: boolean;
    pillGroups: PillGroup[];
    dimension?: string;
    textProperty?: string;
    todoIndicator?: string;
    helpTopicName?: string;
    onDimensionChange?: (dimension: string | undefined) => void;
    onTextPropertyChange?: (textProperty: string | undefined) => void;
    onInfoClick?: () => void;
  }

  export type TPIDrawerProps = AdditionalProps & Pick<DimensionCardProps, 'useableSubdims'>;
</script>

<script lang="ts">
  let {
    open: openProp = false,
    dimension: dimensionProp,
    todoIndicator: todoIndicatorProp,
    ...props
  }: TPIDrawerProps = $props();

  function getItemById(id: string | undefined): PillGroup | undefined {
    return props.pillGroups.find((item) => item.id === id);
  }

  function getItemByKey(key: string | undefined): PillGroup | undefined {
    return props.pillGroups.find((item) => item.key === key);
  }

  let accProps: AccordionProps<boolean> = {
    onValueChange: (...args) => {
      dimensionProp = getItemById(args[0] as string)?.key;
      props.onDimensionChange?.(dimensionProp);
      return true;
    },
  };

  const accordion = new Accordion(accProps);

  let lastDimensionProp: string | undefined;

  $effect(() => {
    if (dimensionProp === lastDimensionProp) return;

    const key = dimensionProp || lastDimensionProp;
    const item = getItemByKey(key);

    if (item) {
      const action = dimensionProp ? 'expand' : 'collapse';
      accordion.getItem(item)[action]();
    }

    lastDimensionProp = dimensionProp;
  });
</script>

<div class="overflow-hidden rounded-b-md shadow-card">
  {#if openProp}
    <div class="grid gap-4 bg-prim px-3 pb-3 pt-6" transition:slide>
      <div {...accordion.root} class="grid gap-2">
        {#each props.pillGroups as item (item.id)}
          <DimensionCard
            label={item.label}
            score={item.score}
            pills={item.pills}
            disabled={!item.used}
            open={accordion.getItem(item).isExpanded && !accordion.getItem(item).isDisabled}
            useAccordion={{ accordion, item }}
            todoIndicator={todoIndicatorProp === item.key}
            {...props}
          />
        {/each}
      </div>
      {#if props.helpTopicName}
        <FooterMoreActions
          label="Info"
          helpTarget={{ topic: props.helpTopicName, section: undefined, content: undefined }}
        />
      {/if}
    </div>
  {/if}
</div>
